/**
 * Validator for PlaceTile action.
 *
 * Rules (§6.4, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "placeTile".
 *   3. `coord` is unoccupied in the active player's board.
 *   4. `coord` is edge-adjacent to at least one already-placed tile.
 *
 * Adjacency check uses `getNeighbours` from utils/axial — no re-implementation
 * of hex math. There is NO habitat-edge match requirement per §6.4.
 *
 * Note: The spec does NOT mention a special case for the "first placement
 * on an empty board". In practice, every player starts with 3 starter cluster
 * tiles (from initGame), so the board is never empty during normal play.
 * The validator enforces adjacency unconditionally.
 */

import type { PlaceTileAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import { coordKey } from "../types/coords";
import type { GameState } from "../types/game-state";
import { getNeighbours } from "../utils/axial";
import { commonGuards, VALID } from "./common-guards";

export const validatePlaceTile = (
  state: GameState,
  action: PlaceTileAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "placeTile");
  if (guard !== null) {
    return guard;
  }

  const activePlayer = state.players[state.turn.activeSeat];
  if (activePlayer === undefined) {
    return { valid: false, error: "ERR_PHASE_MISMATCH", details: {} };
  }

  const { cells } = activePlayer.board;
  const targetKey = coordKey(action.coord);

  // Check coord is unoccupied
  if (cells[targetKey] !== undefined) {
    return {
      valid: false,
      error: "ERR_TILE_OCCUPIED",
      details: { coord: action.coord },
    };
  }

  // Check adjacency — at least one neighbour must be an occupied cell
  const neighbours = getNeighbours(action.coord);
  const hasAdjacentTile = neighbours.some(
    (n) => cells[coordKey(n)] !== undefined,
  );

  if (!hasAdjacentTile) {
    return {
      valid: false,
      error: "ERR_TILE_NOT_ADJACENT",
      details: { coord: action.coord },
    };
  }

  // Free rotation (optional): when present it must be an integer 0..5.
  // Absent ⇒ 0 (replay-safe). Rotation never affects placement legality.
  const { rotation } = action;
  if (
    rotation !== undefined &&
    !(Number.isInteger(rotation) && rotation >= 0 && rotation <= 5)
  ) {
    return {
      valid: false,
      error: "ERR_INVALID_ROTATION",
      details: { rotation },
    };
  }

  return VALID;
};
